import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Image, X, Loader2 } from 'lucide-react';
import { API } from '../App';

export default function AssetPanel({ frayId, renderEvents = [] }) {
  const [expanded, setExpanded] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [assets, setAssets] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [renderCount, setRenderCount] = useState(0);

  // Process render events from parent (SSE)
  useEffect(() => {
    const latest = renderEvents[renderEvents.length - 1];
    if (!latest) return;
    if (latest.type === 'render_start') {
      setRendering(true);
      setRenderCount(latest.spec_count || 0);
    }
    if (latest.type === 'render_complete') {
      setRendering(false);
      API.get(`/api/fray/${frayId}/assets`).then(res => {
        setAssets(res.data.assets || []);
      }).catch(() => {});
    }
  }, [renderEvents, frayId]);

  if (assets.length === 0 && !rendering) return null;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <>
      {/* Desktop: inline sidebar within flex layout */}
      {/* Mobile: floating toggle + overlay panel */}

      {/* Toggle button — mobile: bottom-right FAB, desktop: hidden when expanded */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`
          fixed z-40 min-h-[44px] min-w-[44px] flex items-center justify-center
          bg-[#1A1A1A] border border-[#2A2A2A]
          md:hidden bottom-4 right-4 rounded-full shadow-lg
          ${rendering ? 'animate-pulse' : ''}
        `}
        data-testid="asset-panel-toggle-mobile"
        aria-label={expanded ? 'Close asset panel' : `Open asset panel${assets.length > 0 ? ` (${assets.length} assets)` : ''}`}
      >
        <Image className="w-5 h-5 text-[#FF4D00]" />
        {assets.length > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF4D00] text-black text-[10px] font-bold rounded-full flex items-center justify-center">
            {assets.length}
          </span>
        )}
      </button>

      {/* Desktop sidebar — sits inside the flex parent */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 224, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="hidden md:flex flex-col border-l border-[#2A2A2A] bg-[#1A1A1A] shrink-0 overflow-hidden"
            data-testid="asset-panel-desktop"
          >
            <div className="p-3 flex-1 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold font-['Barlow_Condensed'] flex items-center gap-1.5">
                  <Image className="w-3.5 h-3.5 text-[#FF4D00]" /> ASSETS
                </h3>
                <button onClick={() => setExpanded(false)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#F5F5F5] -mr-2"
                  data-testid="asset-panel-close"
                  aria-label="Close asset panel">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {rendering && (
                <div className="p-2.5 bg-[#FF4D00]/10 border border-[#FF4D00]/30 mb-3 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 text-[#FF4D00] animate-spin" />
                  <span className="text-[10px] text-[#FF4D00] font-mono">Generating {renderCount} assets...</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                {assets.map(asset => (
                  <button key={asset.id} onClick={() => setLightbox(asset)}
                    className="aspect-square bg-[#0F0F0F] border border-[#2A2A2A] hover:border-[#FF4D00]/50 overflow-hidden transition-colors"
                    data-testid={`asset-thumb-${asset.id}`}
                    aria-label={`View asset: ${asset.brief || 'Asset'}`}>
                    <img src={`${backendUrl}/api/fray/${frayId}/assets/${asset.id}/thumb`}
                      alt={asset.brief || 'Asset'} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop collapsed toggle */}
      {!expanded && (assets.length > 0 || rendering) && (
        <button
          onClick={() => setExpanded(true)}
          className="hidden md:flex w-8 border-l border-[#2A2A2A] bg-[#1A1A1A] shrink-0 flex-col items-center justify-center gap-1 hover:bg-[#222]"
          data-testid="asset-panel-expand"
          aria-label={`Expand asset panel${assets.length > 0 ? ` (${assets.length} assets)` : ''}`}
        >
          <Image className="w-3.5 h-3.5 text-[#FF4D00]" />
          {assets.length > 0 && (
            <span className="text-[9px] font-mono text-[#FF4D00]">{assets.length}</span>
          )}
          {rendering && <Loader2 className="w-3 h-3 text-[#FF4D00] animate-spin" />}
        </button>
      )}

      {/* Mobile overlay panel */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="md:hidden fixed inset-x-0 bottom-0 z-50 bg-[#1A1A1A] border-t border-[#2A2A2A] max-h-[60vh] overflow-y-auto"
            data-testid="asset-panel-mobile"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold font-['Barlow_Condensed'] flex items-center gap-2">
                  <Image className="w-4 h-4 text-[#FF4D00]" /> Assets
                  {assets.length > 0 && (
                    <span className="text-[10px] font-mono text-[#A0A0A0]">({assets.length})</span>
                  )}
                </h3>
                <button onClick={() => setExpanded(false)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[#A0A0A0]"
                  data-testid="asset-panel-close-mobile"
                  aria-label="Close asset panel">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {rendering && (
                <div className="p-3 bg-[#FF4D00]/10 border border-[#FF4D00]/30 mb-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-[#FF4D00] animate-spin" />
                  <span className="text-xs text-[#FF4D00]">Generating {renderCount} assets...</span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                {assets.map(asset => (
                  <button key={asset.id} onClick={() => setLightbox(asset)}
                    className="aspect-square bg-[#0F0F0F] border border-[#2A2A2A] hover:border-[#FF4D00]/30 overflow-hidden min-h-[44px]"
                    data-testid={`asset-thumb-mobile-${asset.id}`}
                    aria-label={`View asset: ${asset.brief || 'Asset'}`}>
                    <img src={`${backendUrl}/api/fray/${frayId}/assets/${asset.id}/thumb`}
                      alt={asset.brief || 'Asset'} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox — full screen, works on mobile and desktop */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)} data-testid="asset-lightbox" role="dialog" aria-label="Asset lightbox">
          <button className="absolute top-4 right-4 min-h-[44px] min-w-[44px] flex items-center justify-center text-white"
            onClick={() => setLightbox(null)} data-testid="lightbox-close" aria-label="Close lightbox">
            <X className="w-6 h-6" />
          </button>
          <img src={`${backendUrl}/api/fray/${frayId}/assets/${lightbox.id}`}
            alt={lightbox.brief || 'Asset'}
            className="max-w-full max-h-[80vh] object-contain"
            onClick={e => e.stopPropagation()} />
          {lightbox.brief && (
            <p className="absolute bottom-4 left-4 right-4 text-center text-sm text-[#A0A0A0]">{lightbox.brief}</p>
          )}
        </div>
      )}
    </>
  );
}
